import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  ApiError,
  Button,
  Eyebrow,
  Field,
  Input,
  Select,
  Spinner,
  formatDateTime,
} from '@busbook/ui';
import { useTenant } from '../lib/tenant.js';
import { api } from '../lib/api.js';
import { useT } from '../i18n/index.js';

/**
 * Refund requests and complaints (docs/05 "Booking management").
 *
 * This is where a passenger lands when self-service cancellation is no longer
 * allowed, so the screen leads with what they can still do rather than with the
 * refusal.
 */

interface DisputeMessage {
  id: string;
  author: 'CUSTOMER' | 'STAFF';
  body: string;
  createdAt: string;
}

interface DisputeView {
  id: string;
  type: string;
  subject: string;
  status: string;
  resolutionNote: string | null;
  createdAt: string;
  messages: DisputeMessage[];
}

const STATUS_COPY: Record<string, string> = {
  OPEN: 'Waiting for the operator',
  IN_REVIEW: 'Being looked at',
  RESOLVED_REFUNDED: 'Refund approved',
  RESOLVED_REJECTED: 'Refund not approved',
  CLOSED: 'Closed',
};

export function Dispute() {
  const t = useT();
  const { reference = '' } = useParams();
  const { settings, branding } = useTenant();

  const [disputes, setDisputes] = useState<DisputeView[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ type: 'REFUND_REQUEST', subject: '', message: '' });
  const [reply, setReply] = useState('');

  const load = useCallback(async () => {
    try {
      setDisputes(
        await api.request<DisputeView[]>(`/public/bookings/${reference}/disputes`, {
          method: 'GET',
          auth: false,
        }),
      );
    } catch (err) {
      setError(
        err instanceof ApiError && err.code === 'NOT_FOUND'
          ? 'We could not find that booking on this device. Open your ticket from the confirmation email first.'
          : 'Could not load your requests.',
      );
      setDisputes([]);
    }
  }, [reference]);

  useEffect(() => {
    void load();
  }, [load]);

  const open = (disputes ?? []).find((d) => d.status === 'OPEN' || d.status === 'IN_REVIEW');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.request(`/public/bookings/${reference}/disputes`, {
        method: 'POST',
        auth: false,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(form),
      });
      setForm({ type: 'REFUND_REQUEST', subject: '', message: '' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not send your request.');
    } finally {
      setBusy(false);
    }
  }

  async function sendReply(disputeId: string, event: FormEvent) {
    event.preventDefault();
    if (!reply.trim()) return;
    setBusy(true);
    try {
      await api.request(`/public/bookings/${reference}/disputes/${disputeId}/messages`, {
        method: 'POST',
        auth: false,
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ body: reply.trim() }),
      });
      setReply('');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'We could not send your message.');
    } finally {
      setBusy(false);
    }
  }

  if (disputes === null) {
    return (
      <div className="grid place-items-center py-20">
        <Spinner label={t('c.loading')} />
      </div>
    );
  }

  return (
    <section className="px-4 py-6">
      <Eyebrow>Booking {reference}</Eyebrow>
      <h1 className="mt-1 text-xl font-semibold tracking-tight">{t('d.report')}</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--color-muted)' }}>
        {branding.appName} handles refunds and complaints directly. You will get an email when
        they reply.
      </p>

      {error && (
        <div className="mt-4">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {disputes.length > 0 && (
        <ul className="mt-6 flex flex-col gap-4">
          {disputes.map((dispute) => (
            <li
              key={dispute.id}
              className="rounded-xl border p-4"
              style={{ borderColor: 'var(--color-line)', background: 'var(--color-surface)' }}
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <p className="text-sm font-semibold">{dispute.subject}</p>
                <span className="text-xs font-medium" style={{ color: 'var(--color-primary)' }}>
                  {STATUS_COPY[dispute.status] ?? dispute.status}
                </span>
              </div>

              <ol className="mt-3 flex flex-col gap-3">
                {dispute.messages.map((message) => (
                  <li key={message.id}>
                    <p className="text-[0.6875rem] uppercase tracking-wider" style={{ color: 'var(--color-faint)' }}>
                      {message.author === 'STAFF' ? branding.appName : 'You'} ·{' '}
                      {formatDateTime(message.createdAt, settings.timezone)}
                    </p>
                    <p className="mt-0.5 text-sm whitespace-pre-wrap">{message.body}</p>
                  </li>
                ))}
              </ol>

              {dispute.status !== 'CLOSED' && (
                <form onSubmit={(event) => sendReply(dispute.id, event)} className="mt-4 flex gap-2">
                  <Input
                    aria-label={t('d.addMessage')}
                    placeholder={t('d.addMessage')}
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                  />
                  <Button type="submit" size="md" loading={busy} disabled={!reply.trim()}>
                    {t('c.send')}
                  </Button>
                </form>
              )}
            </li>
          ))}
        </ul>
      )}

      {!open && (
        <form onSubmit={submit} className="mt-7 flex flex-col gap-4">
          <h2 className="text-sm font-semibold">{t('d.newRequest')}</h2>
          <Field label={t('d.about')} htmlFor="d-type">
            <Select id="d-type" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="REFUND_REQUEST">I need a refund</option>
              <option value="COMPLAINT">{t('d.wentWrong')}</option>
              <option value="OTHER">{t('d.somethingElse')}</option>
            </Select>
          </Field>
          <Field label={t('d.subject')} htmlFor="d-subject">
            <Input
              id="d-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              required
              minLength={3}
              maxLength={120}
            />
          </Field>
          <Field label={t('d.tellThem')} htmlFor="d-message">
            <textarea
              id="d-message"
              className="min-h-28 w-full rounded-lg border p-3 text-[0.9375rem]"
              style={{
                borderColor: 'var(--color-line-strong)',
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
              }}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              required
              minLength={3}
              maxLength={4000}
            />
          </Field>
          <Button type="submit" size="lg" block loading={busy}>
            {t('d.sendRequest')}
          </Button>
        </form>
      )}
    </section>
  );
}
